// canvas/cream-layout.js: restyle step 2, the cream table layout.
//
// A port of the chosen mockup's layout() (docs/design-2026-10/restyle-cream/
// shared/app.js): a top bar, a board (the felt) that holds a square block of
// four trick cells with a seat tag beside each, a 44 px controls row, and
// your hand at the bottom (bottom right on landscape phones).
//
// The mockup measured its seat tags in the DOM. Here tags are sized from a
// text model instead: Tiny5 has whole-pixel advances at 16 px (measured in
// a headless browser, see TINY5_16 below), so a tag's size is a pure
// function of its words. That keeps the layout testable in Node, and the
// DOM tag renders the same rows at the same size.
//
// Card sizes (C1) come from the window alone: the [hand, trick] pair the
// mockup's rule (its GEO table, pick order per mode, block shapes) picks
// with the mockup's own tags (GUS, ATOM, CAPRICA CALLED). The real tags
// must then fit those sizes: names wrap to two lines, a two-word badge
// takes two lines, a tag's padding tightens, the block may take another
// shape. Only when none of that fits does the trick card step down, one
// size at a time. For screens smaller than the mockup's (down to 320x568
// and 460x320) the same search continues with smaller in-between card
// sizes (S1-S5) and more block shapes ('side' and 'rows' on phones).
// Bidding (C9): the bid buttons and the dealer line take the controls
// row and grow upward only into free felt (arrangeBid).
//
// Every box is in board coordinates: (0, 0) is the board's outer top-left
// corner. `board` itself is in page coordinates.

/** Smallest text the table uses, CSS px (C11). */
export const MIN_TEXT_PX = 16;

// Card sizes: the mockup GEO full sizes, then smaller in-between sizes
// with the S card's shape (92 x 124).
export const CARD_SIZES = {
  XL: { w: 128, h: 172 },
  L: { w: 115, h: 155 },
  M: { w: 104, h: 140 },
  S: { w: 92, h: 124 },
  S1: { w: 80, h: 108 },
  S2: { w: 68, h: 92 },
  S3: { w: 60, h: 81 },
  S4: { w: 52, h: 70 },
  // Trick cells only: the mockup's XS card (rank and suit, no art), for
  // the smallest landscape screens.
  S5: { w: 44, h: 60 },
};

// Exposed part of each hand card (mockup steps, then the smaller sizes;
// phones keep at least 44 px so every card is a 44 px tap target).
const STEPS = {
  desk: { XL: 80, L: 72, S: 48, S1: 44, S2: 40 },
  phone: { L: 62, M: 58, S: 48, S1: 44, S2: 44, S3: 44, S4: 44 },
};

// [hand, trick] pairs, best first. The first part of each list is the
// mockup's own; the rest continues with the smaller sizes.
const TRIES = {
  desk: [['XL', 'XL'], ['L', 'L'], ['L', 'S'], ['S', 'S'], ['S', 'S1'], ['S1', 'S1'], ['S1', 'S2'], ['S2', 'S2'], ['S2', 'S3'], ['S2', 'S4']],
  phone: [['L', 'L'], ['L', 'M'], ['M', 'M'], ['S', 'M'], ['S', 'S'], ['S', 'S1'], ['S1', 'S1'], ['S1', 'S2'], ['S2', 'S2'], ['S2', 'S3'], ['S2', 'S4'], ['S3', 'S4'], ['S3', 'S5']],
  land: [['S', 'M'], ['S', 'S'], ['S', 'S1'], ['S1', 'S1'], ['S1', 'S2'], ['S2', 'S2'], ['S2', 'S3'], ['S2', 'S4'], ['S3', 'S4'], ['S3', 'S5'], ['S4', 'S5']],
};

/** The mockup's rule for long seat names: more than 7 letters show the first 6 and a dot. */
export function shortName(name) {
  const chars = [...name];
  return chars.length > 7 ? `${chars.slice(0, 6).join('')}.` : name;
}

// Top bar heights (CSS px, border included), the mockup's. A narrow
// landscape bar drops words, not height (cream-table.css).
export function headHeight(mode) {
  if (mode === 'desk') return 72;
  if (mode === 'phone') return 108;
  return 52;
}

/** The mockup's mode rule. */
export function layoutMode(vw, vh) {
  if (vw >= 900 && vh >= 600) return 'desk';
  return vw > vh ? 'land' : 'phone';
}

// ---- Text model ----

// Tiny5 advance widths at 16 px, measured in headless Chromium with the
// font from client/public/fonts. Every advance is a whole pixel, so text
// widths add up exactly. Other sizes scale by px / 16 (24 px gives whole
// pixels too, since every advance is even).
const TINY5_16 = {
  A: 10, B: 10, C: 10, D: 10, E: 10, F: 10, G: 10, H: 10, I: 4, J: 8, K: 10, L: 10, M: 12,
  N: 10, O: 10, P: 10, Q: 10, R: 10, S: 10, T: 8, U: 10, V: 12, W: 12, X: 12, Y: 12, Z: 10,
  0: 8, 1: 8, 2: 8, 3: 8, 4: 8, 5: 8, 6: 8, 7: 8, 8: 8, 9: 8,
  ' ': 4, '.': 4, '-': 8, _: 8, "'": 4, '/': 6, '?': 8, '!': 4, '&': 10, '(': 6, ')': 6,
  ',': 4, ':': 4, ';': 4, '#': 12, '+': 8, '·': 4, '…': 12, '@': 10, '"': 8, '%': 10,
  '*': 8, '=': 8, '<': 8, '>': 8, '[': 6, ']': 6,
};
// Silkscreen Bold advances at 16 px (same measurement), for headings.
const SILK_16 = {
  A: 14, B: 14, C: 14, D: 14, E: 12, F: 12, G: 14, H: 14, I: 8, J: 14, K: 14, L: 12, M: 16,
  N: 16, O: 14, P: 14, Q: 14, R: 14, S: 14, T: 12, U: 14, V: 16, W: 16, X: 16, Y: 16, Z: 12,
  0: 14, 1: 12, 2: 14, 3: 14, 4: 14, 5: 14, 6: 14, 7: 14, 8: 14, 9: 14, ' ': 8, '.': 8, "'": 8, '-': 12,
};
// A character a face does not have is drawn by a fallback face; count it
// as a full em so the box is never too small for it.
const UNKNOWN_ADVANCE = 16;

/**
 * Width of `text` in Tiny5 (or Silkscreen Bold) at `px` (a multiple of
 * 8), CSS px.
 *
 * @param {string} text
 * @param {number} px
 * @param {'tiny5'|'silk'} [face]
 */
export function textWidth(text, px, face = 'tiny5') {
  const table = face === 'silk' ? SILK_16 : TINY5_16;
  let w = 0;
  for (const ch of text) w += table[ch] ?? UNKNOWN_ADVANCE;
  return Math.round((w * px) / 16);
}

/** The announcement keeps this far from every seat tag and from the up-card. */
const ANNOUNCE_CLEAR = 8;

/**
 * The trump announcement's box (the mockup's announce box): the suit
 * icon, the suit's name, ARE TRUMP and who called it, over the trick
 * grid. While the up-card sits in the dealer's cell (the dealer's
 * discard) it goes in the cell beside the up-card's row, centred over
 * the up-card, or failing that across that row; on a grid too small for
 * it, over the free felt above or below the trick block. It keeps 8 px
 * from the up-card, and from every seat tag on desktop (elsewhere it
 * never covers one). The biggest type that fits wins; ARE
 * TRUMP goes before the icon moves above the words; the maker line
 * always shows (wrapped, at most three lines).
 *
 * @param {CreamLayout} L
 * @param {string|null} avoidSlot
 * @param {string} word   the suit word, e.g. HEARTS
 * @param {string} maker  e.g. BOT HAL CALLED IT
 */
export function trumpAnnounceBox(L, avoidSlot, word, maker) {
  const g = L.grid;
  const avoid = avoidSlot ? L.cells[avoidSlot] : null;
  /** @type {Array<Rect & {centreX?: number, kind: string}>} */
  const rooms = [];
  if (avoid) {
    const top = avoid.y > g.y + 4;
    const rowRoom = top
      ? { x: g.x, y: g.y, w: g.w, h: avoid.y - g.y - ANNOUNCE_CLEAR }
      : { x: g.x, y: avoid.y + avoid.h + ANNOUNCE_CLEAR, w: g.w, h: g.y + g.h - avoid.y - avoid.h - ANNOUNCE_CLEAR };
    rooms.push({ ...rowRoom, x: avoid.x, w: avoid.w, centreX: avoid.x + avoid.w / 2, kind: 'over-upcard' });
    rooms.push({ ...rowRoom, kind: 'row' });
  } else {
    rooms.push({ x: g.x, y: g.y, w: g.w, h: g.h, kind: 'grid' });
  }
  // A grid too small for it (the smallest screens): the free felt above,
  // below, right or left of the trick block, inside the block's region.
  const R = L.region;
  const B = L.block;
  rooms.push({ x: R.x, y: R.y, w: R.w, h: B.y - ANNOUNCE_CLEAR - R.y, kind: 'felt' });
  rooms.push({ x: R.x, y: B.y + B.h + ANNOUNCE_CLEAR, w: R.w, h: R.y + R.h - (B.y + B.h + ANNOUNCE_CLEAR), kind: 'felt' });
  rooms.push({ x: B.x + B.w + ANNOUNCE_CLEAR, y: R.y, w: R.x + R.w - (B.x + B.w + ANNOUNCE_CLEAR), h: R.h, kind: 'felt' });
  rooms.push({ x: R.x, y: R.y, w: B.x - ANNOUNCE_CLEAR - R.x, h: R.h, kind: 'felt' });
  // Landscape with the controls row beside the block: the felt above it
  // (below the side status row, when that is there).
  const C = L.ctrl;
  if (C.x >= B.x + B.w) {
    const top = L.status ? L.status.y + L.status.h + ANNOUNCE_CLEAR : L.felt.y + 4;
    rooms.push({ x: C.x, y: top, w: C.w, h: C.y - ANNOUNCE_CLEAR - top, kind: 'felt' });
  }
  const desk = L.mode === 'desk';
  /** @type {Array<[number, number, number]>} big, small (0: no ARE TRUMP), icon cell px */
  const sizes = desk
    ? [[48, 24, 5], [32, 16, 4], [24, 16, 3], [16, 16, 2], [24, 0, 3], [16, 0, 2]]
    : [[32, 16, 4], [24, 16, 3], [16, 16, 2], [24, 0, 3], [16, 0, 2]];
  /** @type {Array<[number, number, number]>} */
  const thinSizes = [[16, 0, 2]];
  /** @type {Array<'silk'|'tiny5'>} */
  const allFaces = ['silk', 'tiny5'];
  /** @type {Array<'silk'|'tiny5'>} */
  const thinFaces = ['tiny5'];
  // The frame: the mockup's (4 px border, 6 px gaps); last, before the
  // fallback, a thin one (2 px border, padding and gaps; 16 px Tiny5
  // only), so the words keep their size in a strip of felt.
  const frames = [{ border: 4, gap: 6, thin: false }, { border: 2, gap: 2, thin: true }];
  for (const { border, gap, thin } of frames) {
    for (const raw of rooms) {
      const room = clearOfTags(raw, L);
      if (room.w <= 0 || room.h <= 0) continue;
      for (const face of thin ? thinFaces : allFaces) {
        for (const stack of [false, true]) {
          for (const [big, small, iconPx] of thin ? thinSizes : sizes) {
            const pad = thin ? 2 : face === 'tiny5' ? 4 : big >= 48 ? 16 : big >= 32 ? 10 : 6;
            const icon = 9 * iconPx;
            const chromeW = 2 * border + 2 * pad + (stack ? 0 : icon + 12);
            const textW = Math.max(textWidth(word, big, face), small ? textWidth('ARE TRUMP', small, face) : 0);
            const makerW = maker ? textWidth(maker, 16) : 0;
            const w = Math.min(room.w, chromeW + Math.max(textW, makerW, stack ? icon : 0));
            const inner = w - chromeW;
            if (inner < textW || (stack && inner < icon)) continue;
            const makerLines = maker ? wrapLines(maker, inner, 16) : [];
            if (makerLines.length > 3 || makerLines.some((l) => textWidth(l, 16) > inner)) continue;
            const textH = Math.round(big * 1.25) + (small ? gap + Math.round(small * 1.25) : 0) + (makerLines.length ? gap + 18 * makerLines.length : 0);
            const h = 2 * border + 2 * pad + (stack ? icon + gap + textH : Math.max(textH, icon));
            if (h > room.h) continue;
            const cx = room.centreX ?? room.x + room.w / 2;
            const x = Math.max(room.x, Math.min(Math.round(cx - w / 2), room.x + room.w - w));
            return {
              x, y: room.y + Math.round((room.h - h) / 2), w, h,
              big, small, iconPx, pad, border, gap, makerLines, face, stack, fits: true,
              placement: raw.kind,
            };
          }
        }
      }
    }
  }
  // The supported fallback (the view names it in the console): the
  // smallest type in the first room, which may then cover a neighbour.
  const room = rooms[0];
  const border = 4;
  const w = Math.min(room.w, 2 * border + 8 + Math.max(textWidth(word, 16, 'tiny5'), maker ? textWidth(maker, 16) : 0));
  return {
    x: room.x, y: room.y, w, h: Math.max(0, Math.min(room.h, 2 * border + 8 + 20 + 6 + 18)),
    big: 16, small: 0, iconPx: 2, pad: 4, border, gap: 6, makerLines: maker ? [maker] : [], face: 'tiny5', stack: false, fits: false,
    placement: 'grid',
  };
}

/**
 * `room` made smaller until it keeps ANNOUNCE_CLEAR px from every seat
 * tag: each tag in the way takes the side of the room nearest to it.
 *
 * @param {Rect & {centreX?: number, kind: string}} room
 * @param {CreamLayout} L
 */
function clearOfTags(room, L) {
  let r = { ...room };
  // Desktop: 8 px (the old splash kept 8 px from the partner's fan);
  // phones: never on a tag.
  const c = L.mode === 'desk' ? ANNOUNCE_CLEAR : 1;
  for (const t of Object.values(L.tags)) {
    const hit = t.x < r.x + r.w + c && r.x < t.x + t.w + c && t.y < r.y + r.h + c && r.y < t.y + t.h + c;
    if (!hit) continue;
    // How much each side must give, and keep the cheapest.
    /** @type {Array<[string, number]>} */
    const sides = [
      ['left', t.x + t.w + c - r.x],
      ['right', r.x + r.w - (t.x - c)],
      ['top', t.y + t.h + c - r.y],
      ['bottom', r.y + r.h - (t.y - c)],
    ];
    const cuts = sides.filter(([, v]) => v > 0).sort((a, b) => a[1] - b[1]);
    const [side, v] = cuts[0];
    if (side === 'left') r = { ...r, x: r.x + v, w: r.w - v };
    else if (side === 'right') r = { ...r, w: r.w - v };
    else if (side === 'top') r = { ...r, y: r.y + v, h: r.h - v };
    else r = { ...r, h: r.h - v };
  }
  return r;
}

/**
 * Width of `text` at `px`, CSS px: textWidth (the pixel faces' advance
 * tables) or, in system-font mode, the real system face (the view
 * measures it; creamLayout's `measure` option).
 *
 * @typedef {(text: string, px: number) => number} TextMeasure
 */

/**
 * Break `text` into lines no wider than `maxW`: at spaces first, and a
 * word longer than a line is cut between letters (never dropped).
 *
 * @param {string} text
 * @param {number} maxW
 * @param {number} px
 * @param {TextMeasure} [measure]
 * @returns {string[]}
 */
export function wrapLines(text, maxW, px, measure = textWidth) {
  const words = text.split(/\s+/).filter(Boolean);
  /** @type {string[]} */
  const lines = [];
  let line = '';
  const push = (word) => {
    const next = line ? `${line} ${word}` : word;
    if (measure(next, px) <= maxW) { line = next; return; }
    if (line) { lines.push(line); line = ''; }
    if (measure(word, px) <= maxW) { line = word; return; }
    // Cut a long word between letters.
    let part = '';
    for (const ch of word) {
      if (part && measure(part + ch, px) > maxW) { lines.push(part); part = ''; }
      part += ch;
    }
    line = part;
  };
  for (const word of words) push(word);
  if (line) lines.push(line);
  return lines.length ? lines : [''];
}

// ---- Seat tags ----

/**
 * @typedef {{ name: string, you?: boolean, partner?: boolean,
 *   status?: string|null, count?: number|null }} TagContent
 * @typedef {{ kind: 'name'|'partner'|'status'|'count', text: string, w: number, lines?: string[] }} TagItem
 * @typedef {{ w: number, h: number, rows: TagItem[][], rowLines: number[], dir: 'row'|'col',
 *   font: number, line: number, border: number, padX: number, padT: number,
 *   padB: number, gap: number, rowGap: number, nameLines: number, tight: boolean }} TagBox
 */

const TAG = {
  desk: { font: 24, line: 28, border: 3, padX: 8, padT: 6, padB: 6, gap: 8, rowGap: 4, icon: 12, suit: 18, rolePad: 8 },
  phone: { font: 16, line: 18, border: 3, padX: 4, padT: 6, padB: 4, gap: 6, rowGap: 2, icon: 12, suit: 0, rolePad: 8 },
};
// The tight tag (phones and landscape only, cream-table.css .seat-tag.tight):
// 2 px less side padding, and 2 px less on the PARTNER badge. Used only
// where the mockup's padding leaves a whole-name tag a few pixels short.
const TIGHT = { padX: 2, rolePad: 4 };
const STATUS_PAD = 6;

/**
 * Size a seat tag (mockup .seat-badge): the name (wrapped to `maxW`),
 * a PARTNER badge, a status badge (CALLED, ALONE or SITS OUT) and the
 * cards left. 'col' tags put each item on its own row (the side seats);
 * 'row' tags run them along one row and wrap. In a 'col' tag a two-word
 * badge (SITS OUT) too wide for the tag takes two lines.
 *
 * @param {TagContent} content
 * @param {'row'|'col'} dir
 * @param {number} maxW    the widest the tag may be, border included
 * @param {'desk'|'phone'|'land'} mode
 * @param {boolean} [tight]
 * @param {TextMeasure} [measure]  the words' widths (default: the pixel faces)
 * @returns {TagBox}
 */
export function tagBox(content, dir, maxW, mode, tight = false, measure = textWidth) {
  const desk = mode === 'desk';
  const s = { ...(desk ? TAG.desk : TAG.phone), ...(tight && !desk ? TIGHT : {}) };
  const innerMax = Math.max(s.font, maxW - 2 * s.border - 2 * s.padX);
  const name = (content.you ? 'YOU' : content.name || '').toUpperCase();
  const nameLines = wrapLines(name, innerMax, s.font, measure);
  /** @type {TagItem[]} */
  const items = [];
  if (content.partner) items.push({ kind: 'partner', text: 'PARTNER', w: measure('PARTNER', s.font) + s.rolePad });
  if (content.status) {
    const suitIcon = (content.status === 'CALLED' || content.status === 'ALONE') && s.suit ? 4 + s.suit : 0;
    const one = measure(content.status, s.font) + STATUS_PAD + suitIcon;
    const words = content.status.split(' ');
    if (dir === 'col' && one > innerMax && words.length > 1) {
      items.push({ kind: 'status', text: content.status, lines: words, w: Math.max(...words.map((wd) => measure(wd, s.font))) + STATUS_PAD });
    } else {
      items.push({ kind: 'status', text: content.status, w: one });
    }
  }
  if (content.count != null && !content.you) {
    items.push({ kind: 'count', text: String(content.count), w: s.icon + 4 + measure(String(content.count), s.font) });
  }
  /** @type {TagItem[][]} */
  const rows = nameLines.map((text) => [{ kind: 'name', text, w: measure(text, s.font) }]);
  if (dir === 'col') {
    for (const it of items) rows.push([it]);
  } else {
    // Row tags: the name's last line shares its row with the badges.
    let row = rows.pop() ?? [];
    let used = row.reduce((sum, it, i) => sum + it.w + (i ? s.gap : 0), 0);
    for (const it of items) {
      if (row.length && used + s.gap + it.w > innerMax) { rows.push(row); row = []; used = 0; }
      used += (row.length ? s.gap : 0) + it.w;
      row.push(it);
    }
    if (row.length) rows.push(row);
  }
  const rowLines = rows.map((row) => Math.max(...row.map((it) => it.lines?.length ?? 1)));
  const lineCount = rowLines.reduce((a, b) => a + b, 0);
  const inner = Math.max(...rows.map((row) => row.reduce((sum, it, i) => sum + it.w + (i ? s.gap : 0), 0)));
  return {
    w: inner + 2 * s.padX + 2 * s.border,
    h: lineCount * s.line + (lineCount - 1) * s.rowGap + s.padT + s.padB + 2 * s.border,
    rows, rowLines, dir, font: s.font, line: s.line, border: s.border, padX: s.padX, padT: s.padT, padB: s.padB,
    gap: s.gap, rowGap: s.rowGap, nameLines: nameLines.length, tight: !!(tight && !desk),
  };
}

// ---- Block shapes ----

const SLOTS = /** @type {const} */ (['N', 'W', 'E', 'S']);

/**
 * Tag directions and widest sizes per shape, then the tag boxes around a
 * grid whose top-left is (0, 0). Returns null when a name needs more than
 * `maxLines` lines or two boxes touch.
 *
 * Shapes (the mockup's three, then two more):
 *   square   N tag above, W left, E right, S tag below the grid.
 *   compact  as square, but the S tag sits right of its cell.
 *   side     landscape: N and W tags left of the grid, E and S right.
 *   side2    side with the N tag running in a row (fewer rows).
 *   rows     narrow phones and long names: N and E tags above the grid,
 *            W and S below, each in its own half of the width.
 *
 * Side tags first take their natural width; with `balanced` each side
 * gets half the room left beside the grid, so long names wrap.
 *
 * @param {string} shape
 * @param {{w:number, h:number}} tg   trick card size
 * @param {Record<string, TagContent>} tags
 * @param {{w:number, h:number}} region
 * @param {'desk'|'phone'|'land'} mode
 * @param {{gapS:number, gapV:number, cellGap:number}} g
 * @param {number} maxLines
 * @param {boolean} balanced
 * @param {boolean} [sRow]
 * @param {boolean} [tight]
 * @param {TextMeasure} [measure]
 */
function shapeBlock(shape, tg, tags, region, mode, g, maxLines, balanced, sRow = false, tight = false, measure = textWidth) {
  const gridW = 2 * tg.w + g.cellGap;
  const gridH = 2 * tg.h + g.cellGap;
  const cells = {
    N: { x: 0, y: 0, w: tg.w, h: tg.h },
    E: { x: tg.w + g.cellGap, y: 0, w: tg.w, h: tg.h },
    W: { x: 0, y: tg.h + g.cellGap, w: tg.w, h: tg.h },
    S: { x: tg.w + g.cellGap, y: tg.h + g.cellGap, w: tg.w, h: tg.h },
  };
  const beside = region.w - gridW - 2 * g.gapS;
  const sideMax = balanced ? Math.floor(beside / 2) : beside - 24;
  const colMax = Math.floor((region.w - g.cellGap) / 2);
  /** @type {Record<string, ['row'|'col', number]>} */
  let spec;
  if (shape === 'rows') spec = { N: ['row', colMax], E: ['row', colMax], W: ['row', colMax], S: ['row', colMax] };
  else if (shape === 'side' || shape === 'side2') {
    spec = { N: [shape === 'side2' ? 'row' : 'col', sideMax], W: ['col', sideMax], E: ['col', sideMax], S: [sRow ? 'row' : 'col', sideMax] };
  } else {
    spec = { N: ['row', region.w], W: ['col', sideMax], E: ['col', sideMax], S: shape === 'compact' ? [sRow ? 'row' : 'col', sideMax] : ['row', region.w] };
  }
  /** @type {Record<string, TagBox>} */
  const box = {};
  for (const s of SLOTS) {
    if (spec[s][1] < 24) return null;
    box[s] = tagBox(tags[s] ?? { name: '' }, spec[s][0], spec[s][1], mode, tight, measure);
    if (box[s].nameLines > maxLines) return null;
  }
  // The mockup's centring: cell edge plus half the leftover, rounded.
  const midH = (s, c) => c.x + Math.round((c.w - box[s].w) / 2);
  // A tag taller than its cell stays within the grid's height (the
  // mockup's tags were never that tall, so this changes none of them).
  const midV = (s, c) => {
    const y = c.y + Math.round((c.h - box[s].h) / 2);
    return box[s].h <= gridH ? Math.max(0, Math.min(y, gridH - box[s].h)) : y;
  };
  /** @type {Record<string, {x:number, y:number}>} */
  const at = {};
  if (shape === 'rows') {
    const mid = Math.round(gridW / 2);
    const place = (s, c, left) => {
      const lo = left ? mid - Math.floor(g.cellGap / 2) - colMax : mid + Math.ceil(g.cellGap / 2);
      return Math.max(lo, Math.min(midH(s, c), lo + colMax - box[s].w));
    };
    at.N = { x: place('N', cells.N, true), y: -g.gapV - box.N.h };
    at.E = { x: place('E', cells.E, false), y: -g.gapV - box.E.h };
    at.W = { x: place('W', cells.W, true), y: gridH + g.gapV };
    at.S = { x: place('S', cells.S, false), y: gridH + g.gapV };
  } else if (shape === 'side' || shape === 'side2') {
    at.N = { x: -g.gapS - box.N.w, y: 0 };
    at.W = { x: -g.gapS - box.W.w, y: midV('W', cells.W) };
    at.E = { x: gridW + g.gapS, y: midV('E', cells.E) };
    at.S = { x: gridW + g.gapS, y: midV('S', cells.S) };
  } else {
    at.E = { x: gridW + g.gapS, y: midV('E', cells.E) };
    at.W = { x: -g.gapS - box.W.w, y: midV('W', cells.W) };
    // A wide partner tag (long name, badges) keeps over its cell but does
    // not reach past the left tag: it moves right instead (the mockup's
    // tags never did, so this changes none of them).
    at.N = { x: Math.max(midH('N', cells.N), Math.min(at.W.x, 0)), y: -g.gapV - box.N.h };
    at.S = shape === 'compact'
      ? { x: gridW + g.gapS, y: midV('S', cells.S) }
      : { x: midH('S', cells.S), y: gridH + g.gapV };
  }
  /** @type {Record<string, {x:number, y:number, w:number, h:number}>} */
  const rects = {};
  for (const s of SLOTS) rects[s] = { x: at[s].x, y: at[s].y, w: box[s].w, h: box[s].h };
  // No two tags, and no tag and cell, may touch. Beside its cell your
  // tag may run in a row instead (shorter, under a tall E tag).
  const all = [...SLOTS.map((s) => rects[s]), ...Object.values(cells)];
  for (let i = 0; i < 4; i += 1) {
    for (let j = i + 1; j < all.length; j += 1) {
      if (!touches(all[i], all[j], 2)) continue;
      const sBeside = shape !== 'square' && shape !== 'rows';
      return sBeside && !sRow ? shapeBlock(shape, tg, tags, region, mode, g, maxLines, balanced, true, tight, measure) : null;
    }
  }
  const xs = [0, ...Object.values(rects).map((r) => r.x)];
  const ys = [0, ...Object.values(rects).map((r) => r.y)];
  const x2 = Math.max(gridW, ...Object.values(rects).map((r) => r.x + r.w));
  const y2 = Math.max(gridH, ...Object.values(rects).map((r) => r.y + r.h));
  const bbox = { x: Math.min(...xs), y: Math.min(...ys), w: x2 - Math.min(...xs), h: y2 - Math.min(...ys) };
  return { shape, gridW, gridH, cells, rects, box, bbox };
}

function touches(a, b, gap) {
  return a.x < b.x + b.w + gap && b.x < a.x + a.w + gap && a.y < b.y + b.h + gap && b.y < a.y + a.h + gap;
}

// ---- Bid controls ----

const BID_LINE = 18;  // one line of the dealer line
const BID_GAP = 4;
const BTN_CHROME = 10;  // 3 px border and 2 px padding each side
const BID_ICON = 18;    // the suit icon, 9 x 9 cells of 2 px
/** The visible word on each bid button (the suit shows as an icon). */
export const BID_WORD = { order_up: 'ORDER UP', call: 'CALL', go_alone: 'ALONE', pass: 'PASS' };

/**
 * The width a bid button needs: the word and the icon side by side, or
 * stacked (the word over the icon; 18 + 2 + 18 px fits a 44 px button).
 *
 * @param {{role: string, suit?: string|null}} b
 */
export function bidButtonNeeds(b) {
  const word = BID_WORD[b.role] ?? '';
  const t = textWidth(word, 16);
  const icon = b.suit ? BID_ICON : 0;
  const stacked = BTN_CHROME + Math.max(t, icon);
  // Narrowest: a two-word label on two lines without the icon (ORDER UP:
  // the up-card in the dealer's cell shows the suit). One-word labels
  // keep the stacked icon.
  const words = word.split(' ');
  const narrow = words.length > 1 ? BTN_CHROME + Math.max(...words.map((wd) => textWidth(wd, 16))) : stacked;
  // Last resort (the smallest landscape screens): the suit icon alone,
  // the word for screen readers only; Pass keeps its word.
  const iconOnly = icon ? Math.max(44, BTN_CHROME + icon) : narrow;
  return { inline: BTN_CHROME + t + (icon ? 4 + icon : 0), stacked, narrow, icon: iconOnly };
}

/**
 * How a bid button shows its label at width `w`: 'inline' (word and
 * icon side by side), 'stacked' (word over icon) or 'narrow' (two-line
 * word, no icon).
 *
 * @param {{role: string, suit?: string|null}} b
 * @param {number} w
 * @returns {'inline'|'stacked'|'narrow'|'icon'}
 */
export function bidButtonFace(b, w) {
  const n = bidButtonNeeds(b);
  if (n.inline <= w) return 'inline';
  if (n.stacked <= w) return 'stacked';
  if (n.narrow <= w) return 'narrow';
  return 'icon';
}

/**
 * @typedef {{ x:number, y:number, w:number, h:number, face:'inline'|'stacked'|'narrow'|'icon' }} BidButtonBox
 * @typedef {{ x:number, y:number, w:number, h:number, lines:string[], place:'above'|'row' }} BidLineBox
 * @typedef {{ w:number, h:number, buttons:BidButtonBox[], line:BidLineBox|null, lineHidden?: boolean }} BidArrangement
 */

/**
 * Arrange the bid buttons and the dealer line in a box `width` wide that
 * may grow `upMax` px above the 44 px controls row. Tried in order, with
 * one, two, then three rows of buttons (two rows: the calls and Pass,
 * then the Go alone buttons; three rows: calls, Go alone, Pass):
 *   1. the dealer line on its own line above the buttons;
 *   2. the line beside the last row, on two lines at most.
 * Returns null when none fits. Boxes are relative to the box's top-left.
 *
 * With `lastResort` (no other layout fits the window), the dealer line
 * may go to screen readers only and the buttons may show just their
 * suit icons.
 *
 * @param {{ buttons: Array<{role:string, suit?:string|null}>, line?: string|null }} bid
 * @param {number} width
 * @param {number} upMax
 * @param {boolean} [lastResort]
 * @returns {BidArrangement|null}
 */
export function arrangeBid(bid, width, upMax, lastResort = false) {
  const n = bid.buttons.length;
  if (!n) return null;
  // The last resort packs the buttons 2 px apart instead of 4.
  const GAP = lastResort ? 2 : BID_GAP;
  const needs = bid.buttons.map(bidButtonNeeds);
  const full = (bid.line || '').toUpperCase();
  const texts = full ? [...new Set([full, full.split(' — ')[0]])] : [];
  const all = needs.map((_, i) => i);
  const role = (i) => bid.buttons[i].role;
  const calls = all.filter((i) => role(i) === 'order_up' || role(i) === 'call');
  const pass = all.filter((i) => role(i) === 'pass');
  const alone = all.filter((i) => role(i) === 'go_alone');
  const splits = [[all], [[...calls, ...pass], alone], [calls, alone, pass]]
    .map((rows) => rows.filter((r) => r.length))
    .filter((rows, i) => rows.length === i + 1 || (i === 0 && rows.length === 1))
    .filter((rows, i, list) => list.findIndex((o) => JSON.stringify(o) === JSON.stringify(rows)) === i);
  /** The narrowest a row of buttons can be, and its widths spread over `w`. */
  const rowWidths = (idx, w) => {
    const gaps = (idx.length - 1) * GAP;
    /** @type {Array<'inline'|'stacked'|'narrow'|'icon'>} */
    const modes = lastResort ? ['inline', 'stacked', 'narrow', 'icon'] : ['inline', 'stacked', 'narrow'];
    for (const mode of modes) {
      const need = idx.map((i) => needs[i][mode]);
      const sum = need.reduce((a, b) => a + b, 0) + gaps;
      if (sum <= w) {
        const extra = w - sum;
        const each = Math.floor(extra / need.length);
        let rest = extra - each * need.length;
        return need.map((v) => { const add = each + (rest > 0 ? 1 : 0); rest -= 1; return v + add; });
      }
    }
    return null;
  };
  const minRow = (idx) => Math.min(...['inline', 'stacked', 'narrow'].map((m) => idx.reduce((a, i) => a + needs[i][m], 0))) + (idx.length - 1) * GAP;
  const widthOk = (idx, w) => idx.length === 0 || minRow(idx) <= w;
  const place = (rowsIdx, top, left, w, out) => {
    for (let r = 0; r < rowsIdx.length; r += 1) {
      const widths = rowWidths(rowsIdx[r], w);
      if (!widths) return false;
      let x = left;
      rowsIdx[r].forEach((i, k) => {
        out[i] = { x, y: top + r * (44 + GAP), w: widths[k], h: 44, face: bidButtonFace(bid.buttons[i], widths[k]) };
        x += widths[k] + GAP;
      });
    }
    return true;
  };
  const rowsH = (k) => k * 44 + (k - 1) * GAP;
  // 1. The line above the buttons, on one line.
  const above = texts.find((t) => textWidth(t, 16) <= width) ?? null;
  if (!full || above) {
    for (const rowsIdx of splits) {
      const lineH = full ? BID_LINE + GAP : 0;
      const h = lineH + rowsH(rowsIdx.length);
      if (h - 44 > upMax) continue;
      /** @type {BidButtonBox[]} */
      const buttons = new Array(n);
      if (!place(rowsIdx, lineH, 0, width, buttons)) continue;
      return { w: width, h, buttons, line: above ? { x: 0, y: 0, w: width, h: BID_LINE, lines: [above], place: 'above' } : null };
    }
  }
  if (!full) return null;
  // 2. The line beside one row of buttons (the one that leaves it the
  // most room), at most two lines.
  for (const rowsIdx of splits) {
    const h = rowsH(rowsIdx.length);
    if (h - 44 > upMax) continue;
    const order = rowsIdx.map((_, r) => r).sort((a, b) => minRow(rowsIdx[a]) - minRow(rowsIdx[b]));
    for (const r of order) {
      const row = rowsIdx[r];
      const lineW = width - minRow(row) - 8;
      if (lineW < 40) continue;
      const lines = texts.map((t) => wrapLines(t, lineW, 16)).find((ls) => ls.length <= 2 && ls.every((l) => textWidth(l, 16) <= lineW));
      if (!lines) continue;
      const textW = Math.max(...lines.map((l) => textWidth(l, 16)));
      /** @type {BidButtonBox[]} */
      const buttons = new Array(n);
      const top = r * (44 + GAP);
      let ok = true;
      rowsIdx.forEach((idx, k) => {
        if (k === r) ok = ok && place([idx], top, textW + 8, width - textW - 8, buttons);
        else ok = ok && widthOk(idx, width) && place([idx], k * (44 + GAP), 0, width, buttons);
      });
      if (!ok) continue;
      const lineH = lines.length * BID_LINE;
      return { w: width, h, buttons, line: { x: 0, y: top + Math.round((44 - lineH) / 2), w: textW, h: lineH, lines, place: 'row' } };
    }
  }
  if (!lastResort) return null;
  // 3. Last resort: the line for screen readers only.
  for (const rowsIdx of splits) {
    const h = rowsH(rowsIdx.length);
    if (h - 44 > upMax) continue;
    /** @type {BidButtonBox[]} */
    const buttons = new Array(n);
    if (!place(rowsIdx, 0, 0, width, buttons)) continue;
    return { w: width, h, buttons, line: null, lineHidden: true };
  }
  return null;
}

// ---- The layout ----

/**
 * @typedef {{x:number, y:number, w:number, h:number}} Rect
 * @typedef {{
 *   mode: 'desk'|'phone'|'land', vw: number, vh: number, headH: number,
 *   board: Rect, bw: number, felt: Rect, font: number,
 *   hand: {size:string, w:number, h:number, step:number, x:number, y:number, width:number},
 *   trick: {size:string, w:number, h:number},
 *   shape: string, cells: Record<string, Rect>, tags: Record<string, Rect & TagBox>,
 *   grid: Rect, block: Rect, ctrl: Rect, region: Rect,
 *   fallback: null|'hand'|'bidline'|'short'|'lines'|'smallest',
 *   ref: {hand:string, trick:string},
 *   bid: null | (Rect & { buttons: BidButtonBox[], line: BidLineBox|null, lineHidden: boolean }),
 *   status: null | Rect,
 * }} CreamLayout
 */

// The mockup's own table (shared/app.js NAMES and tagHtml): the card
// sizes it picks for a window are the sizes the table uses there (C1).
const REF_TAGS = {
  S: { name: 'YOU', you: true, count: null },
  W: { name: 'GUS', count: 5 },
  N: { name: 'ATOM', partner: true, count: 5 },
  E: { name: 'CAPRICA', status: 'CALLED', count: 5 },
};
const refCache = new Map();

/**
 * The cream table layout for a `vw` x `vh` window.
 *
 * The card sizes come from the window alone: the sizes the mockup's rule
 * picks with the mockup's own tags (C1). The real tags then must fit
 * those sizes: names wrap to two lines, a two-word badge takes two
 * lines, the tag's padding tightens, the block may take another shape;
 * only when none of that fits does the trick card step down, one size
 * at a time. Past that, the general search (any hand size, names on
 * more lines, then the short-name rule) and last the named fallback.
 *
 * @param {number} vw
 * @param {number} vh
 * @param {Record<string, TagContent>} tags   keyed by slot: S (you), W, N (partner), E
 * @param {{ bid?: { buttons: Array<{role:string, suit?:string|null}>, line?: string|null } | null,
 *   sideStatus?: boolean | 'force', measure?: TextMeasure }} [opts]
 *   bid: the bid buttons (and the dealer line) the controls row must hold.
 *   measure: the seat tags' text widths (system-font mode); unset, the
 *   pixel faces' advance tables (textWidth).
 *   sideStatus: (landscape) the status row moves from the top bar into
 *   the right-hand column, above the controls row (L.status): true only
 *   where it costs no card size, 'force' even where it does.
 * @returns {CreamLayout}
 */
export function creamLayout(vw, vh, tags, opts = {}) {
  if (opts.sideStatus) {
    const plain = creamLayout(vw, vh, tags, { ...opts, sideStatus: false });
    const side = layoutOnce(vw, vh, tags, opts);
    const same = side.status && side.fallback === plain.fallback && side.hand.size === plain.hand.size && side.trick.size === plain.trick.size;
    const forced = opts.sideStatus === 'force' && side.status && side.fallback === null;
    return same || forced ? side : plain;
  }
  return layoutOnce(vw, vh, tags, opts);
}

/**
 * @param {number} vw
 * @param {number} vh
 * @param {Record<string, TagContent>} tags
 * @param {{ bid?: { buttons: Array<{role:string, suit?:string|null}>, line?: string|null } | null, sideStatus?: boolean | 'force', measure?: TextMeasure }} opts
 * @returns {CreamLayout}
 */
function layoutOnce(vw, vh, tags, opts) {
  const W = Math.max(1, Math.floor(vw));
  const H = Math.max(1, Math.floor(vh));
  const ctx = setup(W, H, !!opts.sideStatus);
  // Forced side status: only the arrangements with the controls column.
  ctx.columnOnly = opts.sideStatus === 'force' && ctx.mode === 'land';
  // System-font mode: the real tags are sized in the system face. The
  // card sizes the window picks (the reference search below) stay those
  // of the pixel faces.
  if (opts.measure) ctx.measure = opts.measure;
  const key = `${W}x${H}`;
  if (!refCache.has(key)) {
    const r = searchAll(setup(W, H, false), REF_TAGS, null);
    refCache.set(key, r ? { hs: r.k.hs, ts: r.k.ts, arr: r.k.arr, shape: r.k.b.shape } : null);
  }
  const ref = refCache.get(key);
  const bid = opts.bid && opts.bid.buttons && opts.bid.buttons.length ? opts.bid : null;
  /** @type {any} */
  let found = ref ? fitAtRef(ctx, tags, ref, bid) : null;
  if (!found) {
    found = searchAll(ctx, tags, bid, ref);
    // Even the smallest trick card left no room at the window's hand
    // size: the hand stepped down too. Named, so it never passes silently.
    if (found && ref && found.fallback === null && found.k.hs !== ref.hs) found = { ...found, fallback: 'hand' };
  }
  /** @type {null|'hand'|'bidline'|'short'|'lines'|'smallest'} */
  let fallback = found ? found.fallback : null;
  if (!found) {
    // The supported fallback: the smallest cards, even where the block
    // then overlaps. Named in the console so it never passes silently.
    const tries = TRIES[ctx.mode];
    const [hs, ts] = tries[tries.length - 1];
    console.error(`[cream-layout] nothing fits a ${W}x${H} window; using ${hs}/${ts} cards, which may overlap`);
    fallback = 'smallest';
    const hg = CARD_SIZES[hs];
    const step = ctx.steps[hs];
    const handW = Math.min(4 * step + hg.w, ctx.inner.w - 8);
    const f = /** @type {any} */ (ctx.frame('stack', hg, handW));
    const region = { ...f.region, h: Math.max(0, f.region.h) };
    const b = shapeBlock('rows', CARD_SIZES[ts], tags, { ...region, w: Math.max(region.w, 200) }, ctx.mode, ctx.g, Infinity, true, false, false, ctx.measure)
      ?? shapeBlock('compact', CARD_SIZES[ts], tags, { ...region, w: 2000 }, ctx.mode, ctx.g, Infinity, false, false, false, ctx.measure);
    found = { k: { arr: 'stack', hs, ts, step, handW, b, ...f, region, up: 0, arrangement: null } };
  }
  return finish(ctx, found.k, fallback, ref);
}

/**
 * Window geometry and the frame (hand, controls row, block region) per arrangement.
 *
 * @param {number} W
 * @param {number} H
 * @param {boolean} [sideStatus]
 */
function setup(W, H, sideStatus = false) {
  const mode = /** @type {'desk'|'phone'|'land'} */ (layoutMode(W, H));
  const headH = headHeight(mode);
  const m = mode === 'desk' ? 10 : 4;
  const bw = 5;
  const g = { gapS: mode === 'desk' ? 8 : mode === 'land' ? 4 : 3, gapV: mode === 'desk' ? 6 : 4, cellGap: mode === 'desk' ? 12 : 8 };
  const ctrlH = 44;
  const outer = { x: m, y: headH + m, w: W - 2 * m, h: H - headH - 2 * m };
  const inner = { x: outer.x + bw, y: outer.y + bw, w: outer.w - 2 * bw, h: outer.h - 2 * bw };
  const steps = mode === 'desk' ? STEPS.desk : STEPS.phone;
  const gapC = mode === 'desk' ? 12 : 8;
  // The status row in the right-hand column (landscape): three 24 px lines.
  const statusH = sideStatus && mode === 'land' ? SIDE_STATUS_H : 0;
  /**
   * Hand, controls row and block region for one arrangement, and the
   * most the controls row may grow upward (`upMax`) for a block `bh` tall.
   *   stack   (desk, phone) hand at the bottom, controls row above it,
   *           the block above that.
   *   column  (land, the mockup's) block on the left; controls row and
   *           hand on the right, the hand at the bottom.
   *   strip   (small landscape) hand bottom right, controls row bottom
   *           left, the block above both across the whole width.
   *
   * @param {string} arr
   * @param {{w:number, h:number}} hg
   * @param {number} handW
   * @param {number} [up]   room the controls row takes above itself
   */
  const frame = (arr, hg, handW, up = 0) => {
    if (arr === 'column') {
      const handLeft = inner.x + inner.w - 8 - handW;
      const handTop = inner.y + inner.h - 6 - hg.h;
      // 10 px between the controls row and the hand (the mockup's 8 plus
      // the 2 px the dealer's prompt has always kept as slack).
      const ctrlTop = handTop - 10 - ctrlH;
      const top = inner.y + 4 + (statusH ? statusH + 8 : 0);
      if (ctrlTop - up < top) return null;
      return {
        handLeft, handTop, ctrlTop, ctrlLeft: 0, ctrlW: 0,
        region: { x: inner.x + 6, y: inner.y + 4, w: handLeft - 10 - inner.x - 6, h: inner.h - 8 },
        upMax: () => ctrlTop - top,
      };
    }
    if (arr === 'strip') {
      const handLeft = inner.x + inner.w - 8 - handW;
      const handTop = inner.y + inner.h - 6 - hg.h;
      const ctrlTop = inner.y + inner.h - 6 - ctrlH;
      // Tight margins: this arrangement is only for the smallest screens.
      // 188 px holds four 44 px bid buttons in a row.
      const ctrlLeft = inner.x + 6;
      const ctrlW = Math.min(360, handLeft - 10 - ctrlLeft);
      if (ctrlW < 188) return null;
      const top = Math.min(handTop, ctrlTop - up);
      return {
        handLeft, handTop, ctrlTop, ctrlLeft, ctrlW,
        region: { x: inner.x + 6, y: inner.y + 2, w: inner.w - 12, h: top - 6 - inner.y - 2 },
        upMax: (bh) => ctrlTop - (bh + 8 + inner.y),
      };
    }
    const padB = mode === 'desk' ? 12 : 6;
    // Controls row to hand: the mockup's gap, at least 10 px (prompt slack).
    const gapH = mode === 'desk' ? 12 : 10;
    const padT = mode === 'desk' ? 12 : 6;
    if (handW > inner.w - 8) return null;
    const handTop = inner.y + inner.h - padB - hg.h;
    const ctrlTop = handTop - gapH - ctrlH;
    const regionH = ctrlTop - gapC - inner.y - padT;
    return {
      handLeft: 0, handTop, ctrlTop, ctrlLeft: 0, ctrlW: 0,
      region: { x: inner.x + 4, y: inner.y + padT, w: inner.w - 8, h: regionH - up },
      upMax: (bh) => regionH - bh,
    };
  };
  return { W, H, mode, headH, m, bw, g, ctrlH, outer, inner, steps, frame, statusH, columnOnly: false, measure: textWidth };
}

/** The side status box's height: three 24 px lines, 4 px apart. */
const SIDE_STATUS_H = 3 * 24 + 2 * 4;

/**
 * One candidate: the hand and trick sizes, the arrangement and shape.
 * Null unless the block fits its region and (when bidding) the bid
 * controls fit above the controls row.
 */
function tryFit(ctx, hs, ts, arr, shape, tags, maxLines, balanced, tight, bid, lastResort = false) {
  const hg = CARD_SIZES[hs];
  const tg = CARD_SIZES[ts];
  const step = ctx.steps[hs];
  if (!step || !tg) return null;
  const handW = 4 * step + hg.w;
  const f = ctx.frame(arr, hg, handW);
  if (!f) return null;
  const b = shapeBlock(shape, tg, tags, f.region, ctx.mode, ctx.g, maxLines, balanced, false, tight, ctx.measure);
  if (!b || b.bbox.w > f.region.w || b.bbox.h > f.region.h) return null;
  let { ctrlLeft, ctrlW } = f;
  const { inner } = ctx;
  if (arr === 'column') {
    ctrlLeft = Math.max(f.region.x + Math.round((f.region.w + b.bbox.w) / 2) + 8, inner.x + inner.w - 8 - 360);
    ctrlW = inner.x + inner.w - 8 - ctrlLeft;
    if (ctrlW < 180) return null;
  }
  let up = 0;
  /** @type {BidArrangement|null} */
  let arrangement = null;
  if (bid) {
    // Stack: the bid box spans the controls row's width (on desktop up
    // to 640 px, the board then widens to hold it).
    const width = arr === 'stack'
      ? (ctx.mode === 'desk' ? Math.min(ctx.outer.w - 2 * ctx.bw - 8, 640) : Math.min(inner.w - 8, 440))
      : ctrlW;
    arrangement = arrangeBid(bid, width, f.upMax(b.bbox.h), lastResort);
    if (!arrangement) return null;
    // Desktop and phones: a box no wider than it needs, at least the row.
    if (arr === 'stack') {
      const used = Math.max(...arrangement.buttons.map((r) => r.x + r.w), arrangement.line ? arrangement.line.x + arrangement.line.w : 0);
      const rowW = Math.min(inner.w - 8, 440);
      if (used <= rowW && width > rowW) arrangement = arrangeBid(bid, rowW, f.upMax(b.bbox.h), lastResort) ?? arrangement;
    }
    up = arrangement.h - ctx.ctrlH;
  }
  const fu = up ? ctx.frame(arr, hg, handW, up) : f;
  if (!fu) return null;
  return { arr, hs, ts, step, handW, b, ...fu, ctrlLeft, ctrlW, up, arrangement };
}

/** Arrangements per mode: the mockup's own first. */
function arrangements(mode, columnOnly = false) {
  if (columnOnly) return { BASE: [['column', 'side']], EXTRA: [['column', 'compact'], ['column', 'rows']] };
  const BASE = mode === 'land' ? [['column', 'side']] : [['stack', 'square'], ['stack', 'compact']];
  const EXTRA = mode === 'land'
    ? [['strip', 'side'], ['strip', 'side2'], ['strip', 'compact'], ['strip', 'rows']]
    : [['stack', 'side'], ['stack', 'rows']];
  return { BASE, EXTRA };
}

/**
 * The real tags at the window's card sizes: the hand size stays; the
 * trick card steps down one size at a time only when no shape, wrap or
 * tight tag holds the names on two lines.
 */
function fitAtRef(ctx, tags, ref, bid) {
  const order = Object.keys(CARD_SIZES);
  const { BASE, EXTRA } = arrangements(ctx.mode, ctx.columnOnly);
  const arrs = [[ref.arr, ref.shape], ...[...BASE, ...EXTRA].filter(([a, s]) => !(a === ref.arr && s === ref.shape))]
    .filter(([a]) => !ctx.columnOnly || a === 'column');
  for (const ts of order.slice(order.indexOf(ref.ts))) {
    for (const [arr, shape] of arrs) {
      for (const tight of [false, true]) {
        for (const balanced of [false, true]) {
          const k = tryFit(ctx, ref.hs, ts, arr, shape, tags, 2, balanced, tight, bid);
          if (k) return { k, fallback: null };
        }
      }
    }
  }
  return null;
}

/**
 * The general search (also the reference pick).
 *   1. The mockup's own rule: its arrangement, shapes and size list,
 *      names on at most two lines.
 *   2. Every shape, bigger hands first, names on at most two lines.
 *   3. The same with names on any number of lines.
 *   4. The mockup's short-name rule (more than 7 letters show the first
 *      6 and a dot) for names no layout can hold whole; the tag keeps
 *      the full name for screen readers.
 */
/**
 * @param {any} ctx
 * @param {Record<string, TagContent>} tags
 * @param {any} bid
 * @param {{hs:string, ts:string}|null} [ref]
 */
function searchAll(ctx, tags, bid, ref = null) {
  const { mode, steps } = ctx;
  const tries = TRIES[mode];
  const { BASE, EXTRA } = arrangements(mode, ctx.columnOnly);
  // Past the mockup's own list, a bigger hand comes first: the trick
  // cells shrink (up to three sizes below the hand) before the hand does.
  const order = Object.keys(CARD_SIZES);
  const handFirst = [];
  for (const hs of order) {
    if (!steps[hs]) continue;
    const hi = order.indexOf(hs);
    for (const ts of order) {
      const ti = order.indexOf(ts);
      if (ti >= hi && ti <= hi + 3) handFirst.push([hs, ts]);
    }
  }
  // With the window's own sizes known (ref), the fallbacks never pick a
  // bigger hand or trick card than those.
  const allTries = [...tries.filter(([hs, ts]) => !handFirst.some(([a, b]) => a === hs && b === ts)), ...handFirst]
    .filter(([hs, ts]) => !ref || (order.indexOf(hs) >= order.indexOf(ref.hs) && order.indexOf(ts) >= order.indexOf(ref.ts)))
    .sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]) || order.indexOf(a[1]) - order.indexOf(b[1]));
  const shortTags = Object.fromEntries(Object.entries(tags).map(([k, t]) => [k, { ...t, name: shortName(t.name || '') }]));
  const arrs = [...BASE, ...EXTRA];
  /** @type {Array<{tries: string[][], arrs: string[][], maxLines: number, tags: Record<string, TagContent>, fallback: null|'bidline'|'lines'|'short', tight: boolean[], lastResort?: boolean}>} */
  const passes = [
    { tries, arrs: BASE, maxLines: 2, tags, fallback: null, tight: [false] },
    { tries: allTries, arrs, maxLines: 2, tags, fallback: null, tight: [false, true] },
    ...(bid ? [{ tries: allTries, arrs, maxLines: 2, tags, fallback: /** @type {const} */ ('bidline'), tight: [false, true], lastResort: true }] : []),
    { tries: allTries, arrs, maxLines: 2, tags: shortTags, fallback: 'short', tight: [false, true], lastResort: !!bid },
    { tries: allTries, arrs, maxLines: Infinity, tags, fallback: 'lines', tight: [false, true], lastResort: !!bid },
  ];
  for (const pass of passes) {
    for (const [hs, ts] of pass.tries) {
      for (const [arr, shape] of pass.arrs) {
        for (const tight of pass.tight) {
          for (const balanced of [false, true]) {
            const k = tryFit(ctx, hs, ts, arr, shape, pass.tags, pass.maxLines, balanced, tight, bid, !!pass.lastResort);
            if (k) return { k, fallback: pass.fallback };
          }
        }
      }
    }
  }
  return null;
}

/** Place everything for the chosen candidate, in board coordinates. */
function finish(ctx, k, fallback, ref) {
  const { W, H, mode, headH, bw, outer, ctrlH } = ctx;
  const tg = CARD_SIZES[k.ts];
  const hg = CARD_SIZES[k.hs];
  const b = k.b;
  const bidW = k.arrangement ? k.arrangement.w : 0;
  // Desktop: the board hugs the table and is centred in the window.
  let board = outer;
  if (mode === 'desk') {
    const content = Math.max(b.bbox.w, k.handW, 440, bidW) + 2 * 40 + 2 * bw;
    if (content < outer.w) board = { ...outer, x: Math.round((W - content) / 2), w: content };
  }
  const bin = { x: board.x + bw, y: board.y + bw, w: board.w - 2 * bw, h: board.h - 2 * bw };
  let region = k.region;
  let handLeft = k.handLeft;
  let ctrlLeft = k.ctrlLeft;
  let ctrlW = k.ctrlW;
  if (k.arr === 'stack') {
    region = { ...region, x: bin.x + 4, w: bin.w - 8 };
    handLeft = bin.x + Math.round((bin.w - k.handW) / 2);
    ctrlW = Math.min(bin.w - 8, 440);
    ctrlLeft = bin.x + Math.round((bin.w - ctrlW) / 2);
  }
  const blockLeft = Math.round(region.x + (region.w - b.bbox.w) / 2);
  const inner = ctx.inner;
  const blockTop = k.arr === 'column'
    ? Math.max(region.y, Math.min(Math.round(region.y + (region.h - b.bbox.h) / 2), inner.y + inner.h - 40 - b.bbox.h))
    : Math.round(region.y + (region.h - b.bbox.h) / 2);
  const gx = blockLeft - b.bbox.x;
  const gy = blockTop - b.bbox.y;
  // Board coordinates: subtract the board's outer corner.
  const ox = board.x;
  const oy = board.y;
  const rect = (x, y, w, h) => ({ x: Math.round(x - ox), y: Math.round(y - oy), w: Math.round(w), h: Math.round(h) });
  /** @type {Record<string, Rect>} */
  const cells = {};
  for (const s of SLOTS) cells[s] = rect(gx + b.cells[s].x, gy + b.cells[s].y, tg.w, tg.h);
  /** @type {Record<string, any>} */
  const tagRects = {};
  for (const s of SLOTS) tagRects[s] = { ...b.box[s], ...rect(gx + b.rects[s].x, gy + b.rects[s].y, b.rects[s].w, b.rects[s].h) };
  const ctrl = rect(ctrlLeft, k.ctrlTop, ctrlW, ctrlH);
  /** @type {CreamLayout['bid']} */
  let bid = null;
  if (k.arrangement) {
    const a = k.arrangement;
    const x = k.arr === 'stack' ? Math.round(bin.x - ox + (bin.w - a.w) / 2) : ctrl.x;
    const y = ctrl.y + ctrl.h - a.h;
    bid = {
      x, y, w: a.w, h: a.h,
      buttons: a.buttons.map((r) => ({ ...r, x: x + r.x, y: y + r.y })),
      line: a.line ? { ...a.line, x: x + a.line.x, y: y + a.line.y } : null,
      lineHidden: !!a.lineHidden,
    };
  }
  return {
    mode, vw: W, vh: H, headH, board, bw,
    felt: { x: bw, y: bw, w: board.w - 2 * bw, h: board.h - 2 * bw },
    font: mode === 'desk' ? TAG.desk.font : TAG.phone.font,
    hand: { size: k.hs, w: hg.w, h: hg.h, step: k.step, ...pick(rect(handLeft, k.handTop, k.handW, hg.h)), width: k.handW },
    trick: { size: k.ts, w: tg.w, h: tg.h },
    shape: b.shape, cells, tags: tagRects,
    grid: rect(gx, gy, b.gridW, b.gridH),
    block: rect(blockLeft, blockTop, b.bbox.w, b.bbox.h),
    ctrl,
    region: rect(region.x, region.y, region.w, region.h),
    fallback,
    ref: ref ? { hand: ref.hs, trick: ref.ts } : { hand: k.hs, trick: k.ts },
    bid,
    status: ctx.statusH && k.arr === 'column' ? rect(ctrlLeft, inner.y + 4, ctrlW, ctx.statusH) : null,
  };
}

function pick(r) { return { x: r.x, y: r.y }; }

/**
 * The boxes of `n` hand cards, centred in the hand area (mockup
 * renderHand), left to right.
 *
 * @param {CreamLayout} L
 * @param {number} n
 * @returns {Rect[]}
 */
export function handCardRects(L, n) {
  const used = (Math.max(1, n) - 1) * L.hand.step + L.hand.w;
  const x0 = L.hand.x + Math.round((L.hand.width - used) / 2);
  return Array.from({ length: n }, (_, i) => ({ x: x0 + i * L.hand.step, y: L.hand.y, w: L.hand.w, h: L.hand.h }));
}

/**
 * Lua seat for each slot, the viewer at S (mockup seating: S, then W, N,
 * E clockwise, the same order as the lua seats).
 *
 * @param {number} viewerSeat lua 1-4
 * @returns {Record<'S'|'W'|'N'|'E', number>}
 */
export function seatsBySlot(viewerSeat) {
  const v = Number.isInteger(viewerSeat) && viewerSeat >= 1 && viewerSeat <= 4 ? viewerSeat : 1;
  const at = (i) => ((v - 1 + i) % 4) + 1;
  return { S: at(0), W: at(1), N: at(2), E: at(3) };
}

/** Slot of a lua seat for this viewer. */
export function slotOfSeat(seat, viewerSeat) {
  const map = seatsBySlot(viewerSeat);
  return /** @type {'S'|'W'|'N'|'E'|null} */ (SLOTS.find((s) => map[s] === seat) ?? null);
}

export const _internals = { TINY5_16, TRIES, STEPS, TAG, shapeBlock, REF_TAGS };
